import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanType } from '../../../enum';
import { useQueueLocation } from '../queue/selectors';
export var ShrinkageUnitMode;
(function (ShrinkageUnitMode) {
    ShrinkageUnitMode["Percent"] = "percent";
    ShrinkageUnitMode["Days"] = "days";
    ShrinkageUnitMode["Hours"] = "hours";
})(ShrinkageUnitMode || (ShrinkageUnitMode = {}));
export var SHRINKAGE_PERCENT_MAX = 99.99;
export var SHRINKAGE_DECIMAL = 2;
var getWorkingDaysInMonth = function (month, workingDaysPerWeek) {
    var count = 0;
    var current = new Date(month.getFullYear(), month.getMonth(), 1);
    var end = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    while (current <= end) {
        var day = current.getDay();
        var isWorkingDay = workingDaysPerWeek === 7 || (day >= 1 && day <= workingDaysPerWeek);
        if (isWorkingDay) {
            count++;
        }
        current.setDate(current.getDate() + 1);
    }
    return count;
};
export var getBusinessDays = function (planType, workingDaysPerWeek, periods, periodIdx) {
    var isWeeklyPlan = planType === PlanType.Weekly;
    var validWorkingDaysPerWeek = workingDaysPerWeek != null && workingDaysPerWeek >= 1 && workingDaysPerWeek <= 7 ? workingDaysPerWeek : 5;
    var periodDate = periods === null || periods === void 0 ? void 0 : periods.at(periodIdx);
    var businessDays = isWeeklyPlan
        ? Math.min(validWorkingDaysPerWeek, 7)
        : getWorkingDaysInMonth(moment(periodDate).toDate(), validWorkingDaysPerWeek);
    return businessDays;
};
var getShrinkageUnitFactor = function (unit, paidHoursPerDay, businessDays) {
    switch (unit) {
        case ShrinkageUnitMode.Days:
            return businessDays;
        case ShrinkageUnitMode.Hours:
            return (paidHoursPerDay !== null && paidHoursPerDay !== void 0 ? paidHoursPerDay : 0) * businessDays;
        default:
            return 100;
    }
};
export var getShrinkageDisplayValue = function (percent, unit, paidHoursPerDay, businessDays) {
    if (percent == null || percent === '') {
        return null;
    }
    var factor = getShrinkageUnitFactor(unit, paidHoursPerDay, businessDays);
    if (!factor) {
        return null;
    }
    return (Number(percent) * factor) / 100;
};
export var getShrinkagePercentValue = function (displayValue, unit, paidHoursPerDay, businessDays) {
    if (displayValue == null || displayValue === '') {
        return null;
    }
    var factor = getShrinkageUnitFactor(unit, paidHoursPerDay, businessDays);
    if (!factor) {
        return null;
    }
    return (Number(displayValue) * 100) / factor;
};
export var getShrinkageUnitInputMeta = function (unit) {
    if (unit === ShrinkageUnitMode.Days) {
        return {
            min: 0,
            max: 365,
            suffix: undefined,
            decimal: SHRINKAGE_DECIMAL,
        };
    }
    if (unit === ShrinkageUnitMode.Hours) {
        return {
            min: 0,
            max: 9999,
            suffix: undefined,
            decimal: SHRINKAGE_DECIMAL,
        };
    }
    return {
        min: 0,
        max: SHRINKAGE_PERCENT_MAX,
        suffix: '%',
        decimal: SHRINKAGE_DECIMAL,
    };
};
export var resolveShrinkageStaffSettings = function (queueLocations, queueLocationId) {
    var _a, _b, _c, _d, _e, _f;
    if (queueLocationId) {
        var loc = queueLocations.find(function (l) { return l.id === queueLocationId; });
        return {
            paidHoursPerDay: (_a = loc === null || loc === void 0 ? void 0 : loc.paidHoursPerDay) !== null && _a !== void 0 ? _a : null,
            workingDaysPerWeek: (_b = loc === null || loc === void 0 ? void 0 : loc.workingDaysPerWeek) !== null && _b !== void 0 ? _b : null,
        };
    }
    if (queueLocations.length === 1) {
        return {
            paidHoursPerDay: (_c = queueLocations[0].paidHoursPerDay) !== null && _c !== void 0 ? _c : null,
            workingDaysPerWeek: (_d = queueLocations[0].workingDaysPerWeek) !== null && _d !== void 0 ? _d : null,
        };
    }
    var hasSameSettings = queueLocations.length > 0 &&
        queueLocations.every(function (l) {
            return l.paidHoursPerDay === queueLocations[0].paidHoursPerDay &&
                l.workingDaysPerWeek === queueLocations[0].workingDaysPerWeek;
        });
    if (hasSameSettings) {
        return {
            paidHoursPerDay: (_e = queueLocations[0].paidHoursPerDay) !== null && _e !== void 0 ? _e : null,
            workingDaysPerWeek: (_f = queueLocations[0].workingDaysPerWeek) !== null && _f !== void 0 ? _f : null,
        };
    }
    return { paidHoursPerDay: null, workingDaysPerWeek: null };
};
export var useShrinkageStaffSettings = function (queueLocationId) {
    var queueLocations = useQueueLocation();
    return useMemo(function () { return resolveShrinkageStaffSettings(queueLocations, queueLocationId); }, [queueLocationId, queueLocations]);
};
export var useUnitOptions = function () {
    var t = useTranslation('wizard', { keyPrefix: 'shrinkage' }).t;
    return useMemo(function () { return [
        { id: ShrinkageUnitMode.Percent, name: t('unit.percent') },
        { id: ShrinkageUnitMode.Days, name: t('unit.days') },
        { id: ShrinkageUnitMode.Hours, name: t('unit.hours') },
    ]; }, [t]);
};
